import { Link, useParams } from 'react-router-dom';
import { Button, Card, Container, Row, Col, Spinner } from 'react-bootstrap';
import React, { useState, useEffect, useMemo } from 'react';

import useChatPageEffect from '@/shared/hooks/useChatPageEffect';
import { useChatPageState } from '@/shared/contexts/ChatPageContext';
import logger from '@/shared/utils/logger';

import SpotlightHero from './components/SpotlightHero';
import DataSourceTile from './components/DataSourceTile';
import DataProductTile from './components/DataProductTile';
import StarterPromptsCarousel from './components/StarterPromptsCarousel';
import ScrollIndicator from './components/ScrollIndicator';
import CommunityPicture from './components/CommunityPicture';
import NarrativeSection from './components/NarrativeSection';
import waterSpotlightData from './spotlightData/waterSpotlightData';
import wildfireSpotlightData from './spotlightData/wildfireSpotlightData';
import dataCentersFrontRangeSpotlightData from './spotlightData/dataCentersFrontRangeSpotlightData';
import './SpotlightPage.css';

const ENGINE_BASE_URL = process.env.REACT_APP_ENGINE_URL;

/**
 * Transform an API data source record into the shape DataSourceTile expects.
 */
function toDataSourceTile(source) {
    const parts = [];
    if (source.organization) parts.push(source.organization);
    if (source.update_frequency) {
        parts.push(`Updated: ${source.update_frequency}`);
    }
    if (source.scale_extent) parts.push(source.scale_extent);

    return {
        id: source.id,
        title: source.name,
        description: source.description || '',
        metadata: parts.join(' · ') || '',
        organization: source.organization || '',
        detailUrl: `/data/sources/${source.id}`,
        ctaText: 'Ask Bili about this dataset',
        ctaLink: null,
    };
}

/**
 * Transform an API data product record into the shape DataProductTile expects.
 */
/**
 * Extract the src URL from an HTML embed_code string (e.g. '<iframe src="...">').
 */
function extractEmbedSrc(embedCode) {
    if (!embedCode) return null;
    const match = embedCode.match(/src=["']([^"']+)["']/);
    return match ? match[1] : null;
}

function toDataProductTile(product) {
    const parts = [];
    if (product.organization) parts.push(product.organization);
    if (product.product_type) parts.push(product.product_type);

    const embedSrc = extractEmbedSrc(product.embed_code);
    // Use validated embeddable flag if available; fall back to whether
    // embed_code exists when the health crawl hasn't run yet (null).
    const embeddable =
        product.embeddable !== null ? product.embeddable : !!embedSrc;

    return {
        id: product.id,
        title: product.name,
        description: product.description || '',
        metadata: parts.join(' · ') || '',
        previewUrl: embedSrc || product.url || null,
        dbEmbeddable: embeddable,
        dbPreviewImage: product.preview_image_url || null,
        detailUrl: `/data/products/${product.id}`,
        featured: false,
        image: null,
        primaryCTA: {
            text:
                product.product_type === 'storymap'
                    ? 'View StoryMap'
                    : 'View Product',
            link: product.url || '#',
        },
        secondaryCTA: {
            text: 'Chat with Bili',
            link: null,
        },
    };
}

export default function SpotlightPage() {
    const { topic } = useParams();
    const { openChat } = useChatPageState();

    const [apiSources, setApiSources] = useState(null);
    const [apiProducts, setApiProducts] = useState(null);
    const [loadingSources, setLoadingSources] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(true);

    const spotlightDataMap = {
        water: waterSpotlightData,
        wildfires: wildfireSpotlightData,
        'data-centers-front-range': dataCentersFrontRangeSpotlightData,
    };

    const spotlightData = spotlightDataMap[topic];

    // Fetch real data sources from Engine Flask API (public, no auth needed)
    useEffect(() => {
        if (!topic) return;

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);

        async function fetchSources() {
            try {
                const res = await fetch(
                    `${ENGINE_BASE_URL}/data/sources?spotlight=${topic}&status=active&limit=100`,
                    { signal: controller.signal }
                );
                if (res.ok) {
                    const json = await res.json();
                    setApiSources(json.data_sources.map(toDataSourceTile));
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    logger.error('Failed to fetch data sources:', err);
                }
            } finally {
                setLoadingSources(false);
            }
        }

        async function fetchProducts() {
            try {
                const res = await fetch(
                    `${ENGINE_BASE_URL}/data/products?spotlight=${topic}&status=active&limit=100`,
                    { signal: controller.signal }
                );
                if (res.ok) {
                    const json = await res.json();
                    setApiProducts(json.data_products.map(toDataProductTile));
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    logger.error('Failed to fetch data products:', err);
                }
            } finally {
                setLoadingProducts(false);
            }
        }

        fetchSources();
        fetchProducts();

        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
    }, [topic]);

    // Use API data when the fetch succeeded (non-null), fall back to static
    // placeholder data only when the API hasn't responded yet (null).
    // This ensures a genuine empty result from the API is displayed as empty
    // rather than being masked by the static fallback.
    const dataSources =
        apiSources !== null ? apiSources : (spotlightData?.dataSources ?? []);
    const dataProducts =
        apiProducts !== null
            ? apiProducts
            : (spotlightData?.dataProducts ?? []);

    // Publish page context for the floating chat widget
    const contextDeps = useMemo(
        () => [topic, dataSources.length, dataProducts.length],
        [topic, dataSources.length, dataProducts.length]
    );
    useChatPageEffect(
        {
            page: `${topic}-spotlight`,
            spotlight: topic,
            section: 'spotlight',
            dataSources: dataSources.map((d) => d.title),
            dataProducts: dataProducts.map((d) => d.title),
            topic: spotlightData?.hero?.title || topic,
        },
        contextDeps
    );

    if (!spotlightData) {
        return (
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col xs={12} className="text-center">
                        <h1>Spotlight Not Found</h1>
                        <p>
                            The spotlight topic &quot;{topic}&quot; does not
                            exist.
                        </p>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <section id="spotlight-page" className="min-vh-100">
            <SpotlightHero
                image={spotlightData.hero.image}
                title={spotlightData.hero.title}
                description={spotlightData.hero.description}
                tagline={spotlightData.hero.tagline}
            />

            {spotlightData.publishedOn && (
                <Container fluid>
                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            <p className="text-muted small mb-0 pt-3">
                                Published on: {spotlightData.publishedOn}
                            </p>
                        </Col>
                    </Row>
                </Container>
            )}

            <Container fluid>
                <Row className="justify-content-center">
                    <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                        <hr className="spotlight-page-divider" />
                    </Col>
                </Row>
            </Container>

            {spotlightData.whyItMatters && (
                <section className="why-it-matters-section py-4">
                    <Container fluid>
                        <Row className="justify-content-center">
                            <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                                <h2 className="section-title fw-semibold mb-3">
                                    {spotlightData.whyItMatters.title}
                                </h2>
                                <p className="section-description fs-5 mb-4">
                                    {spotlightData.whyItMatters.intro}
                                </p>

                                <Row className="g-3 mb-5">
                                    {spotlightData.whyItMatters.keyStats.map(
                                        (stat, idx) => (
                                            <Col key={idx} xs={6} md={3}>
                                                <div className="why-stat-card text-center p-3 h-100">
                                                    <div className="why-stat-value fw-bold mb-1">
                                                        {stat.value}
                                                        {stat.unit && (
                                                            <span className="why-stat-unit">
                                                                {' '}
                                                                {stat.unit}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="why-stat-label small mb-2">
                                                        {stat.label}
                                                    </p>
                                                    <a
                                                        href={stat.source.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="why-stat-source"
                                                    >
                                                        {stat.source.name}
                                                    </a>
                                                </div>
                                            </Col>
                                        )
                                    )}
                                </Row>

                                {spotlightData.whyItMatters.narrative.map(
                                    (para, idx) => (
                                        <NarrativeSection
                                            key={idx}
                                            para={para}
                                        />
                                    )
                                )}

                                {spotlightData.communityPicture && (
                                    <div
                                        id="community-picture"
                                        className="community-picture-section py-4"
                                    >
                                        <CommunityPicture
                                            data={
                                                spotlightData.communityPicture
                                            }
                                        />
                                    </div>
                                )}

                                <div className="why-learn-more mt-5">
                                    <h3 className="fs-5 fw-semibold mb-3">
                                        Learn More
                                    </h3>
                                    <Row className="g-3">
                                        {spotlightData.whyItMatters.learnMore.map(
                                            (link, idx) => (
                                                <Col key={idx} xs={12} sm={6}>
                                                    <a
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="why-learn-link d-block text-decoration-none p-3 rounded border h-100"
                                                    >
                                                        <strong className="d-block mb-1">
                                                            {link.name}
                                                        </strong>
                                                        <small className="text-muted">
                                                            {link.description}
                                                        </small>
                                                    </a>
                                                </Col>
                                            )
                                        )}
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </Container>

                    <Container fluid>
                        <Row className="justify-content-center">
                            <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                                <hr className="spotlight-page-divider" />
                            </Col>
                        </Row>
                    </Container>
                </section>
            )}

            {spotlightData.reliefResources && (
                <section className="relief-section py-4">
                    <Container fluid>
                        <Row className="justify-content-center">
                            <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                                <h2 className="section-title fw-semibold mb-3">
                                    {spotlightData.reliefResources.title}
                                </h2>
                                <p className="section-description fs-5 mb-4">
                                    {spotlightData.reliefResources.intro}
                                </p>

                                <Row className="g-4 mb-4">
                                    {spotlightData.reliefResources.fires.map(
                                        (fire, idx) => (
                                            <Col key={idx} xs={12} md={6}>
                                                <Card className="relief-card h-100">
                                                    <Card.Body>
                                                        <Card.Title className="fw-semibold">
                                                            {fire.name}
                                                        </Card.Title>
                                                        <Card.Subtitle className="text-muted mb-3">
                                                            {fire.region}
                                                        </Card.Subtitle>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {fire.links.map(
                                                                (link, li) => (
                                                                    <Button
                                                                        key={li}
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        href={
                                                                            link.url
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {
                                                                            link.label
                                                                        }
                                                                    </Button>
                                                                )
                                                            )}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )
                                    )}
                                </Row>

                                <p className="text-muted small">
                                    {
                                        spotlightData.reliefResources
                                            .attribution.text
                                    }{' '}
                                    <a
                                        href={
                                            spotlightData.reliefResources
                                                .attribution.url
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {
                                            spotlightData.reliefResources
                                                .attribution.name
                                        }
                                    </a>
                                    .
                                </p>

                                <hr className="spotlight-page-divider" />
                            </Col>
                        </Row>
                    </Container>
                </section>
            )}

            {spotlightData.tribute && (
                <section className="tribute-section py-4">
                    <Container fluid>
                        <Row className="justify-content-center">
                            <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                                <h2 className="section-title fw-semibold mb-3">
                                    {spotlightData.tribute.title}
                                </h2>
                                {spotlightData.tribute.paragraphs.map(
                                    (para, idx) => (
                                        <p
                                            key={idx}
                                            className="section-description fs-5 mb-3"
                                        >
                                            {para}
                                        </p>
                                    )
                                )}
                                <hr className="spotlight-page-divider" />
                            </Col>
                        </Row>
                    </Container>
                </section>
            )}

            <section id="data-sources" className="data-sources-section">
                <Container fluid>
                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            <h2
                                id="data-sources-heading"
                                className="section-title fw-semibold mb-3"
                            >
                                {spotlightData.sections.dataSources.title}
                            </h2>
                            <p className="section-description fs-5 mb-4">
                                {spotlightData.sections.dataSources.description}
                            </p>
                        </Col>
                    </Row>

                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            {loadingSources && !apiSources ? (
                                <div className="d-flex justify-content-center py-5">
                                    <Spinner
                                        animation="border"
                                        variant="primary"
                                    />
                                    <span className="visually-hidden">
                                        Loading data sources...
                                    </span>
                                </div>
                            ) : (
                                <div className="scroll-wrapper">
                                    <div className="tiles-scroll-container data-sources-container">
                                        <Row className="flex-nowrap g-0">
                                            {dataSources.map((source) => (
                                                <Col
                                                    key={source.id}
                                                    xs={11}
                                                    sm={9}
                                                    md={7}
                                                    lg={5}
                                                    xl={4}
                                                >
                                                    <DataSourceTile
                                                        title={source.title}
                                                        description={
                                                            source.description
                                                        }
                                                        metadata={
                                                            source.metadata
                                                        }
                                                        organization={
                                                            source.organization
                                                        }
                                                        detailUrl={
                                                            source.detailUrl
                                                        }
                                                        ctaText={source.ctaText}
                                                        ctaLink={source.ctaLink}
                                                        onClick={() =>
                                                            openChat(
                                                                `Tell me about the ${source.title} dataset`
                                                            )
                                                        }
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                    <ScrollIndicator containerSelector=".data-sources-container" />
                                </div>
                            )}
                        </Col>
                    </Row>
                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            <hr className="spotlight-page-divider" />
                        </Col>
                    </Row>
                </Container>
            </section>

            <section id="products" className="data-products-section">
                <Container fluid>
                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            <h2
                                id="data-products-heading"
                                className="section-title fw-semibold mb-3"
                            >
                                {spotlightData.sections.dataProducts.title}
                            </h2>
                            <p className="section-description fs-5 mb-4">
                                {
                                    spotlightData.sections.dataProducts
                                        .description
                                }
                            </p>
                        </Col>
                    </Row>

                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            {loadingProducts && !apiProducts ? (
                                <div className="d-flex justify-content-center py-5">
                                    <Spinner
                                        animation="border"
                                        variant="primary"
                                    />
                                    <span className="visually-hidden">
                                        Loading data products...
                                    </span>
                                </div>
                            ) : (
                                <div className="scroll-wrapper">
                                    <div className="tiles-scroll-container data-products-container">
                                        <Row className="flex-nowrap g-0">
                                            {dataProducts.map((product) => (
                                                <Col
                                                    key={product.id}
                                                    xs={11}
                                                    sm={9}
                                                    md={7}
                                                    lg={5}
                                                    xl={4}
                                                >
                                                    <DataProductTile
                                                        title={product.title}
                                                        description={
                                                            product.description
                                                        }
                                                        metadata={
                                                            product.metadata
                                                        }
                                                        previewUrl={
                                                            product.previewUrl
                                                        }
                                                        embeddable={
                                                            product.dbEmbeddable ===
                                                            true
                                                        }
                                                        previewImage={
                                                            product.dbPreviewImage
                                                        }
                                                        detailUrl={
                                                            product.detailUrl
                                                        }
                                                        featured={
                                                            product.featured
                                                        }
                                                        image={product.image}
                                                        primaryCTA={
                                                            product.primaryCTA
                                                        }
                                                        secondaryCTA={
                                                            product.secondaryCTA
                                                        }
                                                        onChatClick={() =>
                                                            openChat(
                                                                `Tell me about ${product.title}`
                                                            )
                                                        }
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                    <ScrollIndicator containerSelector=".data-products-container" />
                                </div>
                            )}
                        </Col>
                    </Row>
                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            <hr className="spotlight-page-divider" />
                        </Col>
                    </Row>
                </Container>
            </section>

            <section id="ask-bili" className="starter-prompts-section">
                <Container fluid>
                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            <h2
                                id="starter-prompts-heading"
                                className="section-title fw-semibold mb-3"
                            >
                                {spotlightData.sections.starterPrompts.title}
                            </h2>
                            <p className="section-description fs-5 mb-5">
                                {
                                    spotlightData.sections.starterPrompts
                                        .description
                                }
                            </p>

                            <StarterPromptsCarousel
                                prompts={spotlightData.starterPrompts}
                                onPromptClick={(prompt) => openChat(prompt)}
                            />
                        </Col>
                    </Row>
                </Container>
            </section>

            <section className="browse-all-section">
                <Container fluid>
                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            <hr className="spotlight-page-divider" />
                            <div className="text-center py-4">
                                <p className="fs-5 text-muted mb-4">
                                    Want to explore all{' '}
                                    {spotlightData.displayName} data in detail?
                                </p>
                                <Button
                                    as={Link}
                                    to={`/data?spotlight=${topic}`}
                                    variant="primary"
                                    size="lg"
                                    className="rounded-pill px-5"
                                >
                                    Browse All{' '}
                                    {spotlightData.displayName
                                        .split(' ')
                                        .map(
                                            (w) =>
                                                w.charAt(0).toUpperCase() +
                                                w.slice(1)
                                        )
                                        .join(' ')}{' '}
                                    Data
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {spotlightData.communitySlug && (
                <section id="community" className="community-coi-section">
                    <Container fluid>
                        <Row className="justify-content-center">
                            <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                                <hr className="spotlight-page-divider" />
                                <div className="text-center py-4">
                                    <h2 className="section-title fw-semibold mb-3">
                                        Join the Community Discussion
                                    </h2>
                                    <p className="section-description fs-5 mb-4">
                                        Connect with residents, researchers, and
                                        advocates tracking{' '}
                                        {spotlightData.displayName} issues
                                        across Colorado. Share findings, ask
                                        questions, and engage with the data
                                        together.
                                    </p>
                                    <Button
                                        as={Link}
                                        to={`/communities/${spotlightData.communitySlug}`}
                                        variant="outline-primary"
                                        size="lg"
                                        className="rounded-pill px-5"
                                    >
                                        Visit the Community
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
            )}
        </section>
    );
}
